/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import lazySizes from 'lazysizes';
import { gsap } from 'gsap';
import { debounce } from 'lodash';
import Cookies from 'js-cookie';
import Headroom from 'headroom.js';

import Vue from 'vue';
import { enableBodyScroll } from 'body-scroll-lock';
import spring from './utils/animation/spring';
import Router from './router';
import ProjectSliderComponent from './components/project-slider-component.vue';

require('./bootstrap');

require('./components/video-component');
require('./components/intro-component');
require('./components/time-component');
require('./components/ticker-component');
require('./components/slider-component');
require('./components/columns-list-component');
require('./components/vimeo-inline-background');
require('./components/projects-filter-component');
require('./components/header-component');
require('./components/text-switch-component');
require('./components/project-header-component');
require('./components/transition-canvas-component');
require('./components/liquid-header-component');
require('./components/text-split-component');

lazySizes.cfg.loadMode = 3;

window.ColorBleedApp = new Vue({
    el: '#app',
    delimiters: ['${', '}'],

    components: {
        'project-slider': ProjectSliderComponent,
    },

    data() {
        return {
            router: {
                currentSection: null,
                app: null,
                prefetch: true,
            },
            headroom: null,
            spring: {
                values: null,
                set: null,
                stop: null,
            },
            isMobile: false,
            isTablet: false,
            showNavigation: false,
            introduction: true,
            // darkHeader: '1',
            showFooter: '1',
            transition: {
                frameProportion: 1.78, // PNG frame aspect ratio
                frames: 31, // Number of PNG frames
                resize: false,
            },
            animations: [
                {
                    animation: null,
                    stopped: false,
                    title: 'normal',
                },
            ],
            currentSlug: '/',
            touch: false,
        };
    },

    watch: {
        isMobile() {
            EventBus.$emit('is-mobile-changed');
        },
        isTablet() {
            EventBus.$emit('is-tablet-changed');
        },
        showNavigation(visibile) {
            EventBus.$emit('navigation-state-changed', visibile);
        },
        currentSlug(value) {
            // console.log(value);
        },
    },

    mounted() {
        if (Cookies.get('introduction')) {
            this.introduction = false;
        }

        // Prepare instance properties.
        this.showFooter = this.$el.dataset.initialShowFooter;
        this.currentSlug = document.getElementById('page-container').dataset.pageSlug;

        // Headroom
        this.initHeadroom();

        // Prepare router.
        this.router = new Router({
            app: this,
            prefetch: true,
            section: this.$el.dataset.initialSection,
            ignoreComponents: [
                'footer-component',
                'navigation-component',
                'language-selector',
                'header-component',
            ],
        });

        EventBus.$on('page-transition-build-page', () => {
            this.buildPage();
        });

        // Initial detect of mobile/tablet breakpoints
        this.detectMobile();
        EventBus.$on('window-resize', () => {
            this.detectMobile();
        });
        window.addEventListener('resize', this.onWindowResize);

        const isTouchDevice = () => window.matchMedia('(pointer: coarse)').matches;

        this.touch = isTouchDevice();
        this.mobileVideo = window.matchMedia('(max-width: 1023px) and (orientation: portrait)');
        window.cachedProps = {
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
        };

        Vue.nextTick(() => {
            EventBus.$emit('page-transition-build-page');
            EventBus.$emit('page-transition-enter-complete');
        });
    },

    methods: {
        buildPage() {
            Vue.nextTick(() => {
                this.initRevealTriggers();
                EventBus.$emit('build-page:next-tick');
            });
        },

        initRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('[data-reveal]'),
                ...document.querySelectorAll('[data-image-reveal]'),
                ...document.querySelectorAll('[data-light-image-reveal]'),
            ];

            elements.forEach((element) => {
                const { revealDelay, revealManually } = element.dataset;
                if (revealManually === '') {
                    return;
                }

                const startPercentage = '88%';

                gsap.timeline({
                    scrollTrigger: {
                        trigger: element,
                        start: `top ${startPercentage}`,
                        onEnter: () => {
                            if (!revealDelay) {
                                element.classList.add('--revealed');
                                return;
                            }

                            gsap.delayedCall(revealDelay, () => {
                                element.classList.add('--revealed');
                            });
                        },
                    },
                });
            });
        },

        /**
         * Setup the transition.
         *
         * @returns {Void}
         */
        setupTransition() {
            // Query transition elements.
            window.transitionHolder = document.getElementById('transition-layer-ink');
            this.transition.background = document.getElementById('transition-normal');

            EventBus.$on('window-resize', () => {
                window.requestAnimationFrame(this.setTransitionLayerDimensions);
            });

            this.setTransitionLayerDimensions();

            this.buildTransitions();
        },

        buildTransitions() {
            for (let index = 0; index < this.animations.length; index++) {
                const animationObject = this.animations[index];
                this.createBodyMovin(animationObject);
            }
        },

        /**
         * Set the transition background layer dimensions.
         *
         * @returns {Void}
         */
        setTransitionLayerDimensions() {
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            let layerHeight; let layerWidth;

            if (windowWidth / windowHeight > this.transition.frameProportion) {
                layerWidth = windowWidth;
                layerHeight = layerWidth / this.transition.frameProportion;
            } else {
                layerHeight = windowHeight;
                layerWidth = layerHeight * this.transition.frameProportion;
            }

            this.transition.background.style.width = `${layerWidth}px`;
            this.transition.background.style.height = `${layerHeight}px`;
        },

        initHeadroom() {
            const header = document.querySelector('.header__container');
            this.headroom = new Headroom(header);
            this.headroom.init();
        },

        closeIntroduction() {
            gsap.to('.introduction-container', {
                duration: 0.5,
                autoAlpha: 0,
                onComplete: () => {
                    this.introduction = false;
                    enableBodyScroll(document.querySelector('header'));
                    Cookies.set('introduction', 'true');
                },
            });
        },

        circleBounceMouseEnter(e) {
            if (this.touch === false) {
                let circle = e.currentTarget.querySelector('.round-button__circle');

                if (!circle) {
                    circle = e.currentTarget.querySelector('.bounce-button-circle');
                }

                if (!circle) {
                    return;
                }

                spring(circle, {
                    from: 1,
                    to: 0.89,
                    attribute: 'scale',
                    tension: 800,
                    friction: 10,
                });
            }
        },

        circleBounceMouseLeave(e) {
            if (this.touch === false) {
                let circle = e.currentTarget.querySelector('.round-button__circle');

                if (!circle) {
                    circle = e.currentTarget.querySelector('.bounce-button-circle');
                }

                if (!circle) {
                    return;
                }

                spring(circle, {
                    from: 0.89,
                    to: 1,
                    attribute: 'scale',
                    tension: 800,
                    friction: 10,
                    onComplete: () => {
                        console.log('Complete');
                    },
                });
            }
        },

        /**
         * Raised when the client window is resizing.
         *
         * @return {Void}
         */
        onWindowResize: debounce(() => {
            EventBus.$emit('window-resize');

            window.cachedProps = {
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
            };
        }, 150),

        detectMobile() {
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

            if (viewportWidth < 768) {
                this.isTablet = false;
                this.isMobile = true;
                return;
            }

            if (viewportWidth < 1025) {
                this.isTablet = true;
                this.isMobile = false;
                return;
            }

            this.isMobile = false;
            this.isTablet = false;
        },

        returnToVideo() {
            EventBus.$emit('return-to-video');
        },
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
    },
});
